import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useNetworking } from '@/contexts/NetworkingContext';
import { useAuth } from '@/contexts/AuthContext';
import { Users, MessageSquare, Clock, CheckCircle, XCircle, Send, Building, UserPlus } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import ContactSelector from './ContactSelector';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

// 🔥 PHASE 3: Lazy load ConversationsHub pour éviter le chargement au démarrage
const ConversationsHub = React.lazy(() => 
  import('./ConversationsHub').then(m => ({ default: m.ConversationsHub }))
);
import { EventInvitationButton } from '@/components/events/EventInvitationButton';

const NetworkingHub = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { 
    connectionRequests, 
    messages, 
    connections,
    loading, 
    sendMessage, 
    respondToConnectionRequest,
    networkingStats
  } = useNetworking();
  
  const [messageRecipient, setMessageRecipient] = useState('');
  const [messageSubject, setMessageSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [replyToMessage, setReplyToMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('connections');
  const [connectionsPage, setConnectionsPage] = useState(0);
  const [requestsPage, setRequestsPage] = useState(0);
  const PAGE_SIZE = 3;
  const [cancelDialog, setCancelDialog] = useState<{
    isOpen: boolean;
    requestId?: string;
    memberName?: string;
  }>({
    isOpen: false
  });

  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    requestId?: string;
    memberName?: string;
    action?: 'delete';
  }>({
    isOpen: false
  });

  const stats = networkingStats;

  // Handle URL tab parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['conversations', 'connections', 'requests'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const handleSendMessage = async () => {
    if (!messageRecipient || !messageContent) return;

    const success = await sendMessage(
      messageRecipient,
      messageContent,
      messageSubject,
      replyToMessage
    );

    if (success) {
      setMessageRecipient('');
      setMessageSubject('');
      setMessageContent('');
      setIsMessageDialogOpen(false);
      setReplyToMessage(null);
    }
  };

  const handleReply = (message: any) => {
    setMessageRecipient(message.sender_id);
    setMessageSubject(`Re: ${message.subject || 'Message'}`);
    setReplyToMessage(message.id);
    setIsMessageDialogOpen(true);
  };

  const handleMessageContact = (contactId: string) => {
    setMessageRecipient(contactId);
    setMessageSubject('');
    setMessageContent('');
    setReplyToMessage(null);
    setIsMessageDialogOpen(true);
  };

  const handleCancelRequest = async () => {
    if (cancelDialog.requestId) {
      // Cancel connection request functionality would go here
      setCancelDialog({ isOpen: false });
    }
  };

  const handleDeleteRequest = async () => {
    if (deleteDialog.requestId) {
      // Delete connection request functionality would go here
      setDeleteDialog({ isOpen: false });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Chargement du hub networking...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex justify-between items-center mb-4">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:grid-cols-3">
            <TabsTrigger value="conversations" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              <span className="hidden sm:inline">Conversations</span>
              {stats.unreadMessages > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 text-xs flex items-center justify-center">
                  {stats.unreadMessages}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="connections" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Mon Réseau</span>
              <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 text-xs flex items-center justify-center">
                {stats.totalConnections}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="requests" className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              <span className="hidden sm:inline">Demandes</span>
              {stats.pendingRequests > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 text-xs flex items-center justify-center">
                  {stats.pendingRequests}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <Dialog open={isMessageDialogOpen} onOpenChange={setIsMessageDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="primary">
                <Send className="h-4 w-4 mr-2" />
                Nouveau message
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {replyToMessage ? 'Répondre au message' : 'Nouveau message'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Destinataire</label>
                  <ContactSelector
                    connections={connections}
                    value={messageRecipient}
                    onValueChange={setMessageRecipient}
                    disabled={!!replyToMessage}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Sujet</label>
                  <Input
                    value={messageSubject}
                    onChange={(e) => setMessageSubject(e.target.value)}
                    placeholder="Sujet du message"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Message</label>
                  <Textarea
                    value={messageContent}
                    onChange={(e) => setMessageContent(e.target.value)}
                    placeholder="Votre message..."
                    rows={4}
                  />
                </div>
                <Button onClick={handleSendMessage} variant="primary" className="w-full">
                  Envoyer
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <TabsContent value="conversations">
          {/* 🔥 PHASE 3: Suspense pour lazy loading */}
          <React.Suspense fallback={
            <Card className="h-[600px] flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <p>Chargement des conversations...</p>
            </Card>
          }>
            <ConversationsHub />
          </React.Suspense>
        </TabsContent>

        <TabsContent value="connections">
          <div className="space-y-4">
            {connections.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <UserPlus className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">Aucune connexion</p>
                  <p className="text-muted-foreground mb-4">
                    Visitez l'annuaire des membres pour créer vos premières connexions
                  </p>
                  <Button variant="primary" asChild>
                    <a href="/members">Découvrir les membres</a>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Mes connexions ({connections.length})</h3>
                  <span className="text-sm text-muted-foreground">
                    {connectionsPage * PAGE_SIZE + 1}-{Math.min((connectionsPage + 1) * PAGE_SIZE, connections.length)} sur {connections.length}
                  </span>
                </div>
                {connections.slice(connectionsPage * PAGE_SIZE, (connectionsPage + 1) * PAGE_SIZE).map((connection) => (
                  <Card key={connection.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <Avatar className="h-12 w-12 ring-2 ring-primary/10">
                            <AvatarImage src={connection.avatar_url} alt={`${connection.firstname} ${connection.lastname}`} />
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {connection.firstname[0]}{connection.lastname[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <h4 className="font-medium text-foreground">
                              {connection.firstname} {connection.lastname}
                            </h4>
                            {connection.company && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Building className="h-4 w-4" />
                                {connection.role && `${connection.role} chez `}{connection.company}
                              </div>
                            )}
                            <div className="flex items-center gap-4 mt-1">
                              {connection.messageCount !== undefined && (
                                <p className="text-xs text-muted-foreground">
                                  {connection.messageCount} message{connection.messageCount !== 1 ? 's' : ''} échangé{connection.messageCount !== 1 ? 's' : ''}
                                </p>
                              )}
                              <p className="text-xs text-green-600">
                                Connecté
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => handleMessageContact(connection.id)}
                            size="sm"
                            variant="primary"
                          >
                            <Send className="h-4 w-4 mr-2" />
                            Message
                          </Button>
                          <EventInvitationButton
                            memberId={connection.id}
                            memberName={`${connection.firstname} ${connection.lastname}`}
                            size="sm"
                            variant="ghost"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {connections.length > PAGE_SIZE && (
                  <div className="flex items-center justify-center gap-3 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={connectionsPage === 0}
                      onClick={() => setConnectionsPage(p => p - 1)}
                    >
                      ← Précédent
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={(connectionsPage + 1) * PAGE_SIZE >= connections.length}
                      onClick={() => setConnectionsPage(p => p + 1)}
                    >
                      Suivant →
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="requests">
          <div className="space-y-4">
            {connectionRequests.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">Aucune demande de connexion</p>
                  <p className="text-muted-foreground mb-4">
                    Les demandes de connexion apparaîtront ici
                  </p>
                  <Button variant="primary" asChild>
                    <a href="/members">Explorer les membres</a>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">
                    Demandes de connexion ({connectionRequests.filter(r => r.status === 'pending').length} en attente)
                  </h3>
                  <span className="text-sm text-muted-foreground">
                    {requestsPage * PAGE_SIZE + 1}-{Math.min((requestsPage + 1) * PAGE_SIZE, connectionRequests.length)} sur {connectionRequests.length}
                  </span>
                </div>
                {connectionRequests.slice(requestsPage * PAGE_SIZE, (requestsPage + 1) * PAGE_SIZE).map((request) => (
                  <Card key={request.id} className={`hover:shadow-md transition-shadow ${
                    request.status === 'pending' && request.requested_id === user?.id 
                      ? 'ring-2 ring-orange-200 bg-orange-50/30' 
                      : ''
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <Avatar className="h-12 w-12">
                            <AvatarImage 
                              src={
                                request.requester_id === user?.id 
                                  ? request.requested_profile?.avatar_url 
                                  : request.requester_profile?.avatar_url
                              } 
                            />
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {request.requester_id === user?.id ? (
                                <>
                                  {request.requested_profile?.firstname?.[0]}
                                  {request.requested_profile?.lastname?.[0]}
                                </>
                              ) : (
                                <>
                                  {request.requester_profile?.firstname?.[0]}
                                  {request.requester_profile?.lastname?.[0]}
                                </>
                              )}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <h4 className="font-medium">
                              {request.requester_id === user?.id ? (
                                <>
                                  Demande envoyée à{' '}
                                  <span className="text-primary">
                                    {request.requested_profile?.firstname} {request.requested_profile?.lastname}
                                  </span>
                                </>
                              ) : (
                                <>
                                  Demande reçue de{' '}
                                  <span className="text-primary">
                                    {request.requester_profile?.firstname} {request.requester_profile?.lastname}
                                  </span>
                                </>
                              )}
                            </h4>
                            {request.message && (
                              <p className="text-sm text-muted-foreground mt-1 italic">
                                "{request.message}"
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {format(new Date(request.created_at), 'PPp', { locale: fr })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant={
                              request.status === 'accepted' ? 'default' :
                              request.status === 'declined' ? 'destructive' : 'secondary'
                            }
                          >
                            {request.status === 'pending' && 'En attente'}
                            {request.status === 'accepted' && 'Acceptée'}
                            {request.status === 'declined' && 'Déclinée'}
                          </Badge>
                          {request.status === 'pending' && (
                            <div className="flex gap-2">
                              {request.requested_id === user?.id ? (
                                <>
                                  <Button
                                    size="sm"
                                    variant="primary"
                                    onClick={() => respondToConnectionRequest(request.id, 'accepted')}
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Accepter
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => respondToConnectionRequest(request.id, 'declined')}
                                  >
                                    <XCircle className="h-4 w-4 mr-1" />
                                    Décliner
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setCancelDialog({
                                    isOpen: true,
                                    requestId: request.id,
                                    memberName: `${request.requested_profile?.firstname} ${request.requested_profile?.lastname}`
                                  })}
                                  className="border-orange-200 text-orange-600 hover:bg-orange-50"
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Annuler
                                </Button>
                              )}
                            </div>
                           )}
                           {request.status === 'declined' && (
                             <Button
                               size="sm"
                               variant="outline"
                               onClick={() => setDeleteDialog({
                                 isOpen: true,
                                 requestId: request.id,
                                  memberName: request.requester_id === user?.id 
                                    ? `${request.requested_profile?.firstname} ${request.requested_profile?.lastname}`
                                    : `${request.requester_profile?.firstname} ${request.requester_profile?.lastname}`,
                                 action: 'delete'
                               })}
                               className="border-red-200 text-red-600 hover:bg-red-50"
                             >
                               <XCircle className="h-4 w-4 mr-1" />
                               Supprimer
                             </Button>
                           )}
                         </div>
                       </div>
                     </CardContent>
                   </Card>
                ))}
                {connectionRequests.length > PAGE_SIZE && (
                  <div className="flex items-center justify-center gap-3 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={requestsPage === 0}
                      onClick={() => setRequestsPage(p => p - 1)}
                    >
                      ← Précédent
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={(requestsPage + 1) * PAGE_SIZE >= connectionRequests.length}
                      onClick={() => setRequestsPage(p => p + 1)}
                    >
                      Suivant →
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

      </Tabs>

      {/* Cancel Request Confirmation Dialog */}
      <AlertDialog open={cancelDialog.isOpen} onOpenChange={(open) => setCancelDialog(prev => ({ ...prev, isOpen: open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annuler la demande de connexion</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir annuler votre demande de connexion avec {cancelDialog.memberName} ?
              Cette action ne peut pas être annulée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCancelDialog({ isOpen: false })}>
              Non, garder
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelRequest}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Oui, annuler
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Request Confirmation Dialog */}
      <AlertDialog open={deleteDialog.isOpen} onOpenChange={(open) => setDeleteDialog(prev => ({ ...prev, isOpen: open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la demande de connexion</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer définitivement cette demande de connexion avec {deleteDialog.memberName} ?
              Cette action ne peut pas être annulée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialog({ isOpen: false })}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRequest}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default NetworkingHub;